// ──────────────────────────────────────────────
// Hooks/useAIBot.js
// Chat state and network layer for the AI assistant widget (Components/AIBot).
// Owns the messages, the picked lab, loading and error flags, and the session id.
// ──────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { aiBotBaseURL } from "../Utils/ProjectConstants";

// ─── Constants ───

// Sent to the backend as `lab`, so each value must match what the API expects exactly.
const LABS = ["C1", "E1"];

const REQUEST_TIMEOUT_MS = 60000;

// sessionStorage: a refresh keeps the chat, a new tab starts fresh (and picks a lab again).
const SESSION_KEY = "aibot.sessionId";
const MESSAGES_KEY = "aibot.messages";
const LAB_KEY = "aibot.lab";

// Message shape: { id: string, role: "user" | "bot", text: string }
const GREETING = {
  id: "greeting",
  role: "bot",
  text: "Hi! Pick your lab above, then ask me a question and I will do my best to help.",
};

// ─── Helpers ───

// crypto.randomUUID only exists on HTTPS or localhost, so keep a fallback for plain HTTP.
const createId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Storage can throw (private windows, blocked site data). The chat should still work.
const readStorage = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    // Ignored on purpose, the chat just will not survive a refresh.
  }
};

const startSession = () => {
  const id = createId();
  writeStorage(SESSION_KEY, id);
  return id;
};

const loadSession = () => readStorage(SESSION_KEY) || startSession();

const loadMessages = () => {
  try {
    const saved = JSON.parse(readStorage(MESSAGES_KEY));
    return Array.isArray(saved) && saved.length > 0 ? saved : [GREETING];
  } catch (err) {
    return [GREETING];
  }
};

// A stored lab that is no longer in LABS is ignored.
const loadLab = () => {
  const saved = readStorage(LAB_KEY);
  return LABS.includes(saved) ? saved : "";
};

// Backend quirk: the OpenAPI listing says /ask returns a bare JSON string, but the real
// response is { answer: string }. Both are accepted. Anything else is stringified so the
// problem is visible instead of a blank bubble.
const extractAnswer = (payload) => {
  const raw = typeof payload === "string" ? payload : payload?.answer;
  const text = typeof raw === "string" ? raw : JSON.stringify(payload);
  return (text || "").trim() || "I do not have an answer for that.";
};

// ─── Hook ───

/**
* Chat state plus the network call to the FastAPI chatbot (POST /ask).
*
* Request body: { question: string, lab: string, session_id: string }
* Response body: { answer: string }
*
* Nothing can be sent until a lab is picked. After the first question the lab is
* locked (isLabLocked) so one session never mixes labs. clearChat() unlocks it.
* retry() re-asks the last question without adding a second user bubble.
*
* 
@returns
 {{
* messages: Array<{ id: string, role: "user" | "bot", text: string }>,
* isLoading: boolean,
* error: string | null,
* lab: string,
* labs: string[],
* isLabLocked: boolean,
* selectLab: (lab: string) => void,
* sendMessage: (text: string) => void,
* retry: () => void,
* clearChat: () => void
* }}
*/
export default function useAIBot() {
  const [messages, setMessages] = useState(loadMessages),
    [lab, setLab] = useState(loadLab),
    [isLoading, setIsLoading] = useState(false),
    [error, setError] = useState(null),
    [initialSessionId] = useState(loadSession),
    sessionIdRef = useRef(initialSessionId),
    abortRef = useRef(null), // request in flight, null when idle
    lastQuestionRef = useRef(null); // what retry() re-sends

  // The backend may keep history per session_id, so the lab cannot change mid-conversation.
  const isLabLocked =
    Boolean(lab) && messages.some((msg) => msg.role === "user");

  // ─── Persistence ───

  useEffect(() => {
    writeStorage(MESSAGES_KEY, JSON.stringify(messages));
  }, [messages]);

  // Drop any in-flight request when the widget unmounts.
  useEffect(
    () => () => {
      const inFlight = abortRef.current;
      abortRef.current = null;
      if (inFlight) inFlight.abort();
    },
    [],
  );

  // ─── Network ───

  const ask = useCallback(
    async (question) => {
      const controller = new AbortController();
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      abortRef.current = controller;
      setIsLoading(true);
      setError(null);

      try {
        // The chatbot is a separate FastAPI service from the .NET API behind apiBaseURL.
        const res = await fetch(`${aiBotBaseURL}/ask`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question,
            lab,
            session_id: sessionIdRef.current,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          // FastAPI sends 422 with { detail: [{ loc, msg, type }] }. It goes to the
          // console only, the on screen message stays generic.
          throw new Error(`Chatbot API ${res.status}: ${await res.text()}`);
        }

        const answer = extractAnswer(await res.json());

        // A cleared chat must not receive a reply meant for the old one.
        if (abortRef.current !== controller) return;

        setMessages((prev) => [
          ...prev,
          { id: createId(), role: "bot", text: answer },
        ]);
      } catch (err) {
        // Aborted by clearChat or unmount, nobody is waiting for this reply.
        if (controller.signal.aborted && !timedOut) return;

        console.error("[AIBot]", err);
        setError(
          timedOut
            ? "The assistant took too long to answer. Please try again."
            : "I could not reach the assistant. Please try again.",
        );
      } finally {
        clearTimeout(timer);
        // A stale request must not reset the loading flag of a newer one.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [lab],
  );

  // ─── Actions ───

  const selectLab = useCallback(
    (next) => {
      if (isLabLocked || !LABS.includes(next)) return;
      writeStorage(LAB_KEY, next);
      setLab(next);
    },
    [isLabLocked],
  );

  const sendMessage = useCallback(
    (rawText) => {
      const text = rawText.trim();
      // No lab, no question. One request at a time keeps replies in order.
      if (!text || !lab || abortRef.current) return;

      lastQuestionRef.current = text;
      setMessages((prev) => [...prev, { id: createId(), role: "user", text }]);
      ask(text);
    },
    [ask, lab],
  );

  const retry = useCallback(() => {
    if (lastQuestionRef.current && !abortRef.current) {
      ask(lastQuestionRef.current);
    }
  }, [ask]);

  const clearChat = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    // New id so any server side memory for the old session_id starts fresh too.
    sessionIdRef.current = startSession();
    lastQuestionRef.current = null;
    setMessages([GREETING]);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    lab,
    labs: LABS,
    isLabLocked,
    selectLab,
    sendMessage,
    retry,
    clearChat,
  };
}
